<template>
  <div
    class="catalog-product-item"
    :class="{[customClass]: customClass}"
  >
    <UiUser
      class="catalog-product-item__seller"
      :user="productComp.seller"
      size="s"
    />

    <div class="catalog-product-item__card">
      <div class="catalog-product-item__card-top">
        <UiIcon
          v-if="productComp.withBadge"
          name="product-label"
          class="catalog-product-item__card-badge"
        />

        <UiLikesCount
          :likes-count="productComp.likeCount"
          :is-liked="productComp.isLiked"
          :product-id="productComp.id"
          :analytics="{ value: getCommunity(productComp) }"
        />
      </div>

      <nuxt-link
        :to="productComp.url"
        class="catalog-product-item__card-img"
      >
        <picture>
          <source
            :srcset="getImgPath(productComp.imgPath)"
            type="image/webp"
          >
          <img
            class="catalog-product-item__card-img-pic"
            :src="productComp.imgPath"
            :alt="productComp.name"
            :loading="imgLoadLazy ? 'lazy' : undefined"
            @error="onImgError"
          >
        </picture>
      </nuxt-link>

      <div class="catalog-product-item__card-info">
        <nuxt-link
          :to="productComp.url"
          class="catalog-product-item__card-info-brand"
        >
          {{ productComp.brandName }}
        </nuxt-link>

        <div class="catalog-product-item__card-info-name">
          {{ productComp.name }}
        </div>

        <div
          v-if="productComp.sizeType !== 'NO_SIZE'"
          class="catalog-product-item__card-info-size"
        >
          <template v-if="['AGE', 'HEIGHT'].includes(productComp.sizeType)">
            {{ $t('components.catalog.productListItem.age') }} {{ productComp.sizes[0]?.categorySizeValue }}
          </template>

          <template v-else-if="productComp.formattedSize">
            {{ productComp.formattedSize }}
          </template>

          <template v-else-if="productComp.sizes">
            {{ productComp.sizeType }} {{ getProductSizes(productComp.sizes) }}
          </template>
        </div>
      </div>

      <div class="catalog-product-item__card-info-price">
        <span class="catalog-product-item__card-info-price-content">
          <span
            v-if="productComp.price"
            class="catalog-product-item__card-info-price-value"
          >
            {{ numberToCurrency(productComp.price) }}
          </span>

          <span
            v-if="productOldPriceComp"
            class="catalog-product-item__card-info-price-discount"
          >
            {{ numberToCurrency(productOldPriceComp) }}
          </span>
        </span>

        <span
          v-if="productComp.discount"
          class="catalog-product-item__card-info-price-discount-value"
        >
          -{{ productComp.discount }}%
        </span>
      </div>

      <CatalogProductListItemAdd
        v-if="addToCart"
        :product-id="productComp.id"
        :sizes="productComp.sizes"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ProductDTORes, SizeValueDTOReq, PrimaryPageProductDTO } from '~/restAPI/data-contracts';
import { getCommunity } from '~/services/analytics/oskellyAnalytics';

const props = defineProps<{
  customClass?: string
  addToCart?: boolean
  imgLoadLazy?: boolean
  product: ProductDTORes & PrimaryPageProductDTO
}>();

const { onImgError, getImgPath } = useImgCdnPath();
const { numberToCurrency } = useNumberFormatter();

const productComp = computed(() => {
  const { product } = props;

  return {
    ...product,
    id: product.productId ?? product.id,
    url: product.url ?? `/products/${product.id}`,
    likeCount: product.likesCount ?? product.likeCount,
    imgPath: product.primaryImageUrl ?? product.imgPath,
    brandName: product.brand?.name ?? product.brandName,
    oldPrice: product.higherPrice ?? product.oldPrice,
    withBadge: product.conditionId === 1 || product.withBadge,
  };
});

const productOldPriceComp = computed(() => {
  const { product } = props;

  if (!product.productId) return product.oldPrice;

  const {
    price, rrpPrice, startPrice, higherPrice,
  } = product;

  if (higherPrice) return higherPrice;

  if (price && startPrice && startPrice > price) {
    return startPrice;
  }

  if (price && rrpPrice && rrpPrice > price) {
    return rrpPrice;
  }

  return 0;
});

function getProductSizes(sizes: SizeValueDTOReq[]) {
  return sizes && sizes.length ? sizes.map((size) => size.productSizeValue).join('/') : '';
}
</script>

<style scoped lang="scss">
.catalog-product-item {
  margin-bottom: 1.75rem;
  display: flex;
  flex-direction: column;

  &__seller {
    margin-bottom: 8px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    border: 1px solid #f2f2f2;
    height: 100%;
    padding: 4px 4px 20px;

    @media screen and (min-width: 767px) {
      padding: 4px 16px 20px;
    }
  }

  &__card-top {
    display: flex;
    width: 100%;
    align-items: center;
  }

  &:deep(.like) {
    margin-left: auto;

    .like__icon {
      height: auto;
      width: auto;
    }

    .like__icon-svg {
      width: 14px;
      height: 15px;

      @media screen and (min-width: 767px) {
        width: 24px;
        height: 20px;
      }
    }

    .like__count {
      font-size: 12px;
      line-height: 15px;

      @media screen and (min-width: 767px) {
        font-size: 14px;
        line-height: 20px;
      }
    }
  }

  &__card-badge {
    width: 21px;
    height: 19px;

    @media screen and (min-width: 767px) {
      width: 25px;
      height: 25px;
    }
  }

  &__card-img {
    margin: 0 -4px;

    @media screen and (min-width: 767px) {
      margin: 0 -16px;
    }
  }

  &__card-img-pic {
    display: block;
    width: 100%;
    height: 198px;
    object-fit: contain;
    object-position: center;

    @media screen and (min-width: 420px) {
      height: 267px;
    }

    @media screen and (min-width: 767px) {
      height: 340px;
    }
  }

  &__card-info {
    margin: 16px 0 20px;
    width: 100%;

    @media screen and (min-width: 767px) {
      margin: 20px 0;
    }
  }

  &__card-info-brand {
    font-size: 14px;
    line-height: 17px;
    font-weight: 600;
    display: block;
    letter-spacing: .4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }

  &__card-info-name,
  &__card-info-size {
    color: #bdbdbd;
    font-size: 12px;
    line-height: 14px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-top: 4px;

    @media screen and (min-width: 767px) {
      margin-top: 8px;
    }
  }

  &__card-info-price {
    display: flex;
    justify-content: space-between;
    margin-top: auto;
    width: 100%;
  }

  &__card-info-price-content {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
  }

  &__card-info-price-value {
    font-size: 13px;
    line-height: 17px;
    font-weight: 600;
    margin-right: 6px;
    display: block;

    @media screen and (min-width: 767px) {
      font-size: 16px;
      line-height: 19px;
    }
  }

  &__card-info-price-discount {
    font-size: 12px;
    line-height: 15px;
    color: #bdbdbd;
    text-decoration: line-through;
    display: block;

    @media screen and (min-width: 767px) {
      font-size: 14px;
      line-height: 17px;
    }
  }

  &__card-info-price-discount-value {
    font-size: 14px;
    line-height: 17px;
    margin-top: auto;
    margin-left: 4px;
    display: block;
  }
}
</style>
